import { useAppSelector } from 'app/hooks';
import { useGetSectionComponentsQuery } from 'app/services/ahuBoardApi';
import type { IAhuBoardSection, ICmsAhuDoc } from 'models';
import ComponentFormHost from '../../forms/component/ComponentFormHost';

/**
 * Phase 6e1 — components tab HOSTING shell (§7.5, tab.components.js): the
 * per-section `li#forms_component_<id>` rows with the hidden
 * `select_mode_radio` pair + the `.rate-active-mode` Rating label (THE §13
 * row 6/7 receiver node — the live click target now renders inside the form
 * heading; the shell label stays `.hide` exactly like the §50.0 placeholder).
 *
 * Phase 6e2a — the dotted preloader is REPLACED by the per-type component
 * form (ComponentFormHost → §50.1 bodies over the §49 chassis). The form
 * keeps its own preloader until first paint (parity 307).
 *
 * Type normalization per §7.5: uds→user_defined_component,
 * plate→plate_heat_exchanger, rotary→energy_wheel; bare heat/heatex/wheel
 * phantoms never get rows. Dual components host on the PRIMARY section only
 * (sitting_on_id sections skip hosting; activity still reaches them via the
 * bound-section check — parity 140/141).
 *
 * PERF rule (6e1-3): only the ACTIVE section's forms mount their bodies —
 * legacy keeps all forms in the DOM via CSS, but React must not mount every
 * catalogue engine on 15-section units. Row shells always render (ids/classes
 * preserved — parity 348); bodies mount when the row is active.
 */

interface TabComponentsProps {
  sections: IAhuBoardSection[];
  cms: ICmsAhuDoc | undefined;
}

const SKIPPED_TYPES = ['heat', 'heatex', 'wheel'];

const normalizeType = (type: string): string => {
  if (type === 'uds') return 'user_defined_component';
  if (type === 'plate') return 'plate_heat_exchanger';
  if (type === 'rotary') return 'energy_wheel';
  return type;
};

const SectionComponentRows = ({
  section,
  sections,
  cms,
}: {
  section: IAhuBoardSection;
  sections: IAhuBoardSection[];
  cms: ICmsAhuDoc | undefined;
}) => {
  const { data: rows } = useGetSectionComponentsQuery(section.id);
  const currentTab = useAppSelector((s) => s.ahuBoard.selection.currentTab);
  const currentSectionId = useAppSelector((s) => s.ahuBoard.selection.currentSectionId);

  const currentSection = sections.find((s) => s.id === currentSectionId);
  // §13 row 1 — resolveComponentHostSectionId (3-step sitting fallback).
  const isActive =
    currentTab === 'components' &&
    (currentSectionId === section.id ||
      currentSection?.sitting_on_id === section.id ||
      section.sitting_on_id === currentSectionId);

  // Dual components host on primary only (§7.5).
  if (section.sitting_on_id) return null;

  return (
    <>
      {(rows ?? [])
        .filter((row) => !SKIPPED_TYPES.includes(String(row.type ?? '')))
        .map((row) => {
          const type = normalizeType(String(row.type ?? ''));
          return (
            <li
              key={row.id}
              id={`forms_component_${row.id}`}
              className={`component component_${section.id}${isActive ? ' active' : ''}`}
              {...{ data_section_id: section.id }}
              data-type={type}
            >
              <input
                type="radio"
                className="select_mode_radio hidden"
                data-id={row.id}
                readOnly
              />
              <input
                type="radio"
                className="select_mode_radio hidden"
                data-id={2}
                readOnly
              />
              {/* §50.0 placeholder label (hidden) — the live §13 rows 6/7
                  click target is the form heading's .rate-active-mode. */}
              <label className="active-mode rate-active-mode hide">Rating</label>
              {isActive ? (
                <ComponentFormHost
                  row={row}
                  section={section}
                  sections={sections}
                  cms={cms}
                />
              ) : (
                <div className="preloader dotted-preloader active" />
              )}
            </li>
          );
        })}
    </>
  );
};

const TabComponents = ({ sections, cms }: TabComponentsProps) => (
  <>
    {sections.map((section) => (
      <SectionComponentRows
        key={section.id}
        section={section}
        sections={sections}
        cms={cms}
      />
    ))}
  </>
);

export default TabComponents;
